import React, {useEffect, useState}                         from 'react'
import {Box, Container, Typography, useTheme}               from '@mui/material'
import {useNavigate}                                        from 'react-router-dom'
import {alpha}                                              from '@mui/material/styles'
import {ChevronRight}                                       from '@mui/icons-material'
import {Helmet}                                             from 'react-helmet-async'
import {doGet}                                              from '../../util/do-fetch'
import {logger}                                             from '../../util/log-utils'
import CategoryIcon                                         from '../CategoryIcon'

const log = logger('Shop', 1)

/*
 * /prep — pick a category (162).
 *
 * Built from the same pieces as the prepkit dashboard rather than its own idiom: bg.surface cards on
 * the page, one hairline, radius.lg, shadow.card, and a hover that lifts. Someone arriving here from
 * the dashboard should recognise the furniture.
 *
 * The gradient is used ONCE per card, in the icon tile, and nowhere else. Each category owns a
 * two-colour brand gradient and there are eight of them on screen; putting that on card borders or
 * fills as well would turn a chooser into a paint chart. The tile is small, it is the one element
 * that is genuinely per-category, and it is the same tile the dashboard header uses for the kit the
 * student ends up owning — so the colour means the same thing in both places.
 *
 * What the hover borrows from the category is only its FIRST colour, at low alpha, on the border.
 * That is enough to tell you the card is live and which one you are on without repeating the
 * gradient.
 */

const CategoryCard = ({category, custom, onClick}) => {
	const accent = category.color || custom.indigo.primary

	return (
		<Box
			component="button"
			type="button"
			data-ga="shop-category"
			onClick={() => onClick(category)}
			sx={{
				font: 'inherit',
				textAlign: 'left',
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				gap: 2,
				p: 2.25,
				cursor: 'pointer',
				background: custom.bg.surface,
				border: `1px solid ${custom.border.color}`,
				borderRadius: `${custom.radius.lg}px`,
				boxShadow: custom.shadow.card,
				transition: 'transform 150ms, box-shadow 150ms, border-color 150ms',
				'&:hover': {
					transform: 'translateY(-2px)',
					boxShadow: custom.shadow.pop,
					borderColor: alpha(accent, 0.5),
				},
				// the lift is decorative; keep a real focus ring for keyboards
				'&:focus-visible': {
					outline: `2px solid ${accent}`,
					outlineOffset: 2,
				},
			}}
		>
			<CategoryIcon
				category={category}
				color={category.color}
				color2={category.color2}
				size={48}
				radius={custom.radius.md}
			/>

			<Typography sx={{
				flex: 1,
				minWidth: 0,
				fontSize: custom.fontSize.lg,
				fontWeight: custom.fontWeight.bold,
				color: custom.text.main,
				lineHeight: 1.3,
			}}>
				{category.name}
			</Typography>

			{/* the same "this leads somewhere" mark the dashboard's HUD cards use */}
			<ChevronRight sx={{flexShrink: 0, color: custom.text.muted}}/>
		</Box>
	)
}

function Shop() {

	const navigate = useNavigate()
	const theme = useTheme()
	const custom = theme.custom
	const [categories, setCategories] = useState(null)

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await doGet(`/mini/shop`)
				let data = await response.json()
				data.sort((a, b) => a.id - b.id)
				setCategories(data)
			}
			catch (err) {
				log.error(err)
			}
		}
		log.debug(`calling fetchData()`)
		fetchData()
	}, [])

	/*
	 * Into the category, not out to the marketing site (163). The hand-off moved down a level: this
	 * now lists the kits in the category and links onward per kit, so the last choice made inside
	 * the app is the actual exam rather than the subject area.
	 */
	const handleClickCategory = (category) => {
		navigate(`/prep/${category.token}`)
	}

	return (
		<Container maxWidth="lg" sx={{pt: {xs: 4, md: 8}, pb: {xs: 4, md: 8}}}>
			<Helmet><title>{'Birdsy - Categories'}</title></Helmet>

			{/*
			  * Left-aligned, sized above the token scale — the same treatment as the dashboard's
			  * greeting (073). Centred h2 made this read as a marketing page rather than as a step in
			  * the app, which is what it is.
			  */}
			<Box sx={{mb: {xs: 3, md: 4}}}>
				<Typography sx={{
					fontSize: {xs: 24, md: 28},
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
					lineHeight: 1.25,
				}}>
					Choose a category
				</Typography>
				<Typography sx={{mt: 0.5, fontSize: custom.fontSize.md, color: custom.text.muted}}>
					Select your area of focus to explore exam prep content.
				</Typography>
			</Box>

			{/*
			  * A plain responsive grid, no wrapping panel. The old version put the cards inside a
			  * bordered, filled box, which drew a container around a set of containers.
			  */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'},
				gap: 2,
			}}>
				{categories && categories.map(c => (
					<CategoryCard
						key={`cat-${c.id}`}
						category={c}
						custom={custom}
						onClick={handleClickCategory}
					/>
				))}
			</Box>
		</Container>
	)
}

export default Shop
